import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "../components/ui/table";
import { Building2, Search, Users, Calendar, Crown } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const Companies = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (user?.role !== "super_admin") {
      return;
    }
    fetchCompanies();
  }, [user]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/companies`, { headers, withCredentials: true });
      setCompanies(response.data);
    } catch (error) {
      toast.error("Error al cargar empresas");
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.company_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPlanBadge = (plan) => {
    switch (plan) {
      case "enterprise":
        return <Badge className="bg-emerald-100 text-emerald-700"><Crown className="w-3 h-3 mr-1" />Enterprise</Badge>;
      case "pro":
        return <Badge className="bg-violet-100 text-violet-700">Pro</Badge>;
      default:
        return <Badge variant="outline">Básico</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="badge-success">Activo</Badge>;
      case "trial":
        return <Badge className="badge-warning">Prueba</Badge>;
      case "past_due":
        return <Badge className="badge-error">Vencido</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (user?.role !== "super_admin") {
    return (
      <Layout title="Acceso Denegado">
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-600">Solo el Super Admin puede acceder a esta sección.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout 
      title="Todas las Empresas" 
      subtitle="Panel de control Super Admin - Vista de todas las empresas registradas"
    >
      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{companies.length}</p>
                <p className="text-sm text-slate-500">Total Empresas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {companies.filter(c => c.plan_status === "active").length}
                </p>
                <p className="text-sm text-slate-500">Activas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {companies.filter(c => c.plan_status === "trial").length}
                </p>
                <p className="text-sm text-slate-500">En Prueba</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
                <Crown className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {companies.filter(c => c.plan === "enterprise").length}
                </p>
                <p className="text-sm text-slate-500">Enterprise</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="mb-6 border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Buscar por nombre o ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-companies-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Companies Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Building2 className="w-12 h-12 mb-3 text-slate-300" />
              <p>No hay empresas registradas</p>
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Empresa</TableHead>
                  <TableHead>RFC</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Max Empleados</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Fecha Creación</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompanies.map((company) => (
                  <TableRow key={company.company_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{company.name}</p>
                          <p className="text-xs text-slate-500 font-mono">{company.company_id}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{company.rfc || '-'}</TableCell>
                    <TableCell>{getPlanBadge(company.plan)}</TableCell>
                    <TableCell className="text-slate-600">{company.max_employees}</TableCell>
                    <TableCell>{getStatusBadge(company.plan_status)}</TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {company.created_at ? new Date(company.created_at).toLocaleDateString('es-MX') : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
};

export default Companies;
